/**
 * WebGL Chart Component
 * High-performance chart using webgl-plot for 1M+ data points @ 60fps
 * Drop-in replacement for ProcessedDataChart with identical props interface
 */

import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
} from '@/shared/components/ui/card';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/shared/components/feedback/Alert';
import { AlertCircle } from 'lucide-react';
import { ProcessedDataChartProps, ZoomState } from './types';
import { LOCALE, CHART_HEIGHT, CHART_PADDING } from './constants';
import { useProcessedData, useDisplayData, DragSelection, HoverPosition } from './hooks';
import { useWebGLInstance } from './hooks/useWebGLInstance';
import { useInteraction } from './hooks/useInteraction';
import { visibleXRange, visibleYRange, fitScaleOffsetY } from './utils/zoomFit';
import { ChartControls } from './components';
import { AxisOverlay } from './overlays/AxisOverlay';
import { SelectionOverlay } from './overlays/SelectionOverlay';
import { PointsOverlay } from './overlays/PointsOverlay';
import { TooltipOverlay, TooltipData } from './overlays/TooltipOverlay';

const WebGLChart: React.FC<ProcessedDataChartProps> = ({
  data,
  seriesName,
  filename,
  sharedBrushRange,
  onSharedBrushChange,
  hideControls = false,
  showPoints: externalShowPoints,
  onPointsVisibilityChange,
  color,
  height,
  hidePointsToggle = false,
}) => {
  const { t } = useTranslation('charts');
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [internalShowPoints, setInternalShowPoints] = useState(false);

  // Use external showPoints when hideControls is true, otherwise use internal state
  const showPointsEnabled = hideControls && externalShowPoints !== undefined
    ? externalShowPoints
    : internalShowPoints;
  const [containerWidth, setContainerWidth] = useState(800);
  // Store the last range we sent to detect our own sync
  const lastSentRangeRef = useRef<{ startTime: number; endTime: number } | null>(null);

  // Zoom state
  const [zoomState, setZoomState] = useState<ZoomState>({
    scaleX: 1,
    scaleY: 1,
    offsetX: 0,
    offsetY: 0,
    xMin: 0,
    xMax: 1,
  });

  // Drag selection state for visual overlay
  const [dragSelection, setDragSelection] = useState<DragSelection | null>(null);

  // Hover state for tooltip
  const [hoverPosition, setHoverPosition] = useState<HoverPosition | null>(null);

  // Process data
  const processedData = useProcessedData(data, seriesName);
  const displayData = useDisplayData(processedData);

  // Calculate chart area dimensions (inside padding)
  const effectiveHeight = height ?? CHART_HEIGHT;
  const chartAreaWidth = Math.max(0, containerWidth - CHART_PADDING.left - CHART_PADDING.right);
  const chartAreaHeight = effectiveHeight - CHART_PADDING.top - CHART_PADDING.bottom;

  // WebGL instance
  const { handleResetZoom, initialBounds } = useWebGLInstance({
    canvasRef,
    displayData,
    processedData,
    seriesName,
    showPoints: showPointsEnabled,
    zoomState,
    onZoomChange: setZoomState,
    chartWidth: chartAreaWidth,
    chartHeight: chartAreaHeight,
    color,
  });

  // Compute a y-fit (scaleY/offsetY) from the visible-x window, closing over
  // the full data + initial bounds. Passed to useInteraction so wheel/pan
  // can refit the y-axis to whatever x-range is currently visible.
  const computeYFit = useCallback(
    (scaleX: number, offsetX: number) => {
      const b = initialBounds.current;
      if (!b) return null;
      const { xMin, xMax } = visibleXRange(b.xMin, b.xMax, scaleX, offsetX);
      const vis = visibleYRange(processedData.timestamps, processedData.values, xMin, xMax);
      if (!vis) return null;
      return fitScaleOffsetY(vis.yMin, vis.yMax, b.yMin, b.yMax);
    },
    [initialBounds, processedData.timestamps, processedData.values],
  );

  // Interaction (zoom, pan)
  useInteraction({
    canvasRef,
    zoomState,
    onZoomChange: setZoomState,
    initialBounds,
    onSharedBrushChange,
    lastSentRangeRef,
    onDragSelectionChange: setDragSelection,
    onHoverChange: setHoverPosition,
    computeYFit,
  });

  // Handle external sync from other charts
  useEffect(() => {
    if (!initialBounds.current) {
      return;
    }

    // If sharedBrushRange is null, reset zoom to default
    if (!sharedBrushRange) {
      setZoomState({
        scaleX: 1,
        scaleY: 1,
        offsetX: 0,
        offsetY: 0,
        xMin: initialBounds.current.xMin,
        xMax: initialBounds.current.xMax,
      });
      return;
    }

    // Check if this is our own sync by comparing values
    // This is more robust than timing-based flags
    if (lastSentRangeRef.current) {
      const isSameRange =
        Math.abs(lastSentRangeRef.current.startTime - sharedBrushRange.startTime) < 1 &&
        Math.abs(lastSentRangeRef.current.endTime - sharedBrushRange.endTime) < 1;

      if (isSameRange) {
        // This is our own sync, clear it and skip
        lastSentRangeRef.current = null;
        return;
      }
    }

    const { xMin, xMax } = initialBounds.current;
    const fullRange = xMax - xMin;
    const visibleStart = sharedBrushRange.startTime / 1000;
    const visibleEnd = sharedBrushRange.endTime / 1000;
    const visibleRange = visibleEnd - visibleStart;

    // Calculate new scale
    const newScaleX = fullRange / visibleRange;

    // Calculate new offset to position the view correctly
    const visibleCenter = (visibleStart + visibleEnd) / 2;
    const dataCenter = (xMin + xMax) / 2;
    const centerOffset = (dataCenter - visibleCenter) / fullRange;
    const newOffsetX = centerOffset * 2 * newScaleX;

    const yFit = computeYFit(newScaleX, newOffsetX);
    setZoomState(prev => ({
      ...prev,
      scaleX: newScaleX,
      offsetX: newOffsetX,
      ...(yFit ? { scaleY: yFit.scaleY, offsetY: yFit.offsetY } : {}),
    }));
  }, [sharedBrushRange, initialBounds, computeYFit]);

  // Resize handling - set initial dimensions and observe changes
  useEffect(() => {
    const container = containerRef.current;
    const canvas = canvasRef.current;
    if (!container || !canvas) return;

    const updateCanvasDimensions = (width: number) => {
      const canvasWidth = Math.max(100, width - CHART_PADDING.left - CHART_PADDING.right);
      const canvasHeight = effectiveHeight - CHART_PADDING.top - CHART_PADDING.bottom;

      // Set pixel buffer dimensions (for high-DPI rendering)
      canvas.width = canvasWidth * window.devicePixelRatio;
      canvas.height = canvasHeight * window.devicePixelRatio;

      // Set display dimensions
      canvas.style.width = `${canvasWidth}px`;
      canvas.style.height = `${canvasHeight}px`;

      setContainerWidth(width);
    };

    // Set initial dimensions immediately
    updateCanvasDimensions(container.clientWidth || 800);

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        updateCanvasDimensions(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, [effectiveHeight]);

  // Toggle points (for internal control when hideControls is false)
  const handleToggleShowPoints = useCallback(() => {
    setInternalShowPoints(prev => !prev);
  }, []);

  // Reset zoom WITH sync to other charts.
  //
  // When a parent provides `onSharedBrushChange`, broadcast `null` rather than
  // this chart's own bounds. Each sibling's sharedBrushRange effect resets to
  // ITS OWN initialBounds, so every chart returns to its full range — a true
  // group reset even when siblings span slightly different x-windows. The
  // local reset then happens through the same effect on this instance, so we
  // don't double-set zoom state here.
  //
  // Without a parent (standalone chart), fall back to a local reset.
  const handleResetZoomWithSync = useCallback(() => {
    if (onSharedBrushChange) {
      onSharedBrushChange(null);
      return;
    }
    handleResetZoom();
  }, [handleResetZoom, onSharedBrushChange]);

  // Format time for display
  const formatTime = (ts: number) => {
    const d = new Date(ts * 1000);
    return new Intl.DateTimeFormat(LOCALE, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
  };

  // Calculate tooltip data from hover position
  const tooltipData = useMemo((): TooltipData | null => {
    if (!hoverPosition || !initialBounds.current || displayData.timestamps.length === 0) {
      return null;
    }

    const { xMin, xMax } = initialBounds.current;

    // Guard against invalid bounds
    if (!Number.isFinite(xMin) || !Number.isFinite(xMax) || xMin >= xMax) {
      return null;
    }

    const xRange = xMax - xMin;

    // Convert normX (-1 to 1) back to timestamp
    // normX = (timestamp - xMin) / xRange * 2 - 1
    // So: timestamp = (normX + 1) / 2 * xRange + xMin
    const hoverTimestamp = (hoverPosition.normX + 1) / 2 * xRange + xMin;

    // Find nearest data point using binary search
    const timestamps = displayData.timestamps;
    const values = displayData.values;

    let left = 0;
    let right = timestamps.length - 1;

    // Binary search for closest timestamp
    while (left < right) {
      const mid = Math.floor((left + right) / 2);
      if (timestamps[mid] < hoverTimestamp) {
        left = mid + 1;
      } else {
        right = mid;
      }
    }

    // Check both left and left-1 to find the closest
    let nearestIdx = left;
    if (left > 0) {
      const diffLeft = Math.abs(timestamps[left] - hoverTimestamp);
      const diffPrev = Math.abs(timestamps[left - 1] - hoverTimestamp);
      if (diffPrev < diffLeft) {
        nearestIdx = left - 1;
      }
    }

    const nearestTimestamp = timestamps[nearestIdx];
    const nearestValue = values[nearestIdx];

    // Skip if value is null
    if (nearestValue === null || !Number.isFinite(nearestValue)) {
      return null;
    }

    // Calculate screen position of the nearest point
    // X position: convert timestamp to screen position
    const normX = (nearestTimestamp - xMin) / xRange * 2 - 1;
    const screenNormX = (normX * zoomState.scaleX + zoomState.offsetX + 1) / 2;
    const screenX = screenNormX * chartAreaWidth;

    // Y position: convert value to screen position
    // Use EXACTLY the same formula as WebGL and PointsOverlay
    const yRangeRaw = processedData.yMax - processedData.yMin || 1;
    const yPadding = yRangeRaw * 0.05;
    const yMinPadded = processedData.yMin - yPadding;
    const yMaxPadded = processedData.yMax + yPadding;
    const yRangePadded = yMaxPadded - yMinPadded;

    // Normalize to [-1, 1] then apply zoom, then convert to screen
    const normalizedY = ((nearestValue - yMinPadded) / yRangePadded) * 2 - 1;
    const zoomedY = normalizedY * zoomState.scaleY + zoomState.offsetY;
    const screenY = ((1 - zoomedY) / 2) * chartAreaHeight;

    // Check if point is visible
    if (screenX < 0 || screenX > chartAreaWidth) {
      return null;
    }

    return {
      x: screenX,
      y: screenY,
      timestamp: nearestTimestamp,
      value: nearestValue,
    };
  }, [hoverPosition, initialBounds, displayData, processedData, zoomState, chartAreaWidth, chartAreaHeight]);

  // In compact mode (custom height supplied by the caller, e.g. STL grid)
  // suppress the built-in CardHeader so the chart fills the entire box.
  const compact = height !== undefined;

  // Early return if no data
  if (!data || data.length === 0 || processedData.timestamps.length === 0) {
    return (
      <Card className="w-full">
        {!compact && (
          <CardHeader className="py-2 sm:py-3">
            <p className="text-xs sm:text-sm md:text-base lg:text-base truncate font-medium text-card-foreground">
              {filename ? `${filename} - ${seriesName}` : seriesName}
            </p>
          </CardHeader>
        )}
        <CardContent>
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>{t("showAllData.alertTitle", { ns: 'dataAdjustments' })}</AlertTitle>
            <AlertDescription>
              {t("showAllData.alertDescription", { ns: 'dataAdjustments' })}
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  const displayStart = processedData.timestamps[0];
  const displayEnd = processedData.timestamps[processedData.timestamps.length - 1];

  return (
    <Card className="w-full">
      {!compact && (
        <CardHeader className="py-2 sm:py-3">
          <p className="text-xs sm:text-sm md:text-base lg:text-base truncate font-medium text-card-foreground">
            {filename ? `${filename} - ${seriesName}` : seriesName}
          </p>
        </CardHeader>
      )}
      <CardContent className="pt-0">
        {/* Controls - hidden when hideControls is true */}
        {!hideControls && (
          <>
            <ChartControls
              totalPoints={processedData.timestamps.length}
              onResetZoom={handleResetZoomWithSync}
              showPointsEnabled={hidePointsToggle ? undefined : showPointsEnabled}
              onToggleShowPoints={hidePointsToggle ? undefined : handleToggleShowPoints}
            />

            {/* Time range */}
            <div className="text-center text-xs sm:text-sm text-muted-foreground mb-1.5 sm:mb-2">
              {t("showAllData.timeRange", { ns: 'dataAdjustments', defaultValue: "Time Range" })}: {formatTime(displayStart)} – {formatTime(displayEnd)}
            </div>

            {/* Zoom instructions */}
            <div className="text-[10px] sm:text-xs text-muted-foreground text-center mb-1.5 sm:mb-2">
              {t("dataChart.zoomHint", "Scroll to zoom, drag to select area, double-click to reset")}
            </div>
          </>
        )}

        {/* Chart container */}
        <div
          ref={containerRef}
          className="relative w-full"
          style={{ height: effectiveHeight }}
        >
          {/* Canvas positioned inside padding area */}
          <canvas
            ref={canvasRef}
            className="absolute"
            style={{
              touchAction: 'none',
              left: CHART_PADDING.left,
              top: CHART_PADDING.top,
              width: chartAreaWidth,
              height: chartAreaHeight,
            }}
          />
          <AxisOverlay
            width={containerWidth}
            height={effectiveHeight}
            xMin={processedData.timestamps[0]}
            xMax={processedData.timestamps[processedData.timestamps.length - 1]}
            yMin={processedData.yMin}
            yMax={processedData.yMax}
            zoomState={zoomState}
            seriesName={seriesName}
          />
          {/* Selection overlay during drag */}
          {dragSelection && (
            <SelectionOverlay
              startX={dragSelection.startX}
              endX={dragSelection.endX}
              startY={dragSelection.startY}
              endY={dragSelection.endY}
              chartHeight={chartAreaHeight}
              active={dragSelection.active}
            />
          )}
          {/* Points overlay when enabled */}
          <PointsOverlay
            timestamps={displayData.timestamps}
            values={displayData.values}
            xMin={processedData.timestamps[0]}
            xMax={processedData.timestamps[processedData.timestamps.length - 1]}
            yMin={processedData.yMin}
            yMax={processedData.yMax}
            zoomState={zoomState}
            chartWidth={chartAreaWidth}
            chartHeight={chartAreaHeight}
            enabled={showPointsEnabled}
            onVisibilityChange={onPointsVisibilityChange}
          />
          {/* Tooltip overlay on hover */}
          <TooltipOverlay
            data={tooltipData}
            chartHeight={chartAreaHeight}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default React.memo(WebGLChart);
